import { Injectable } from '@angular/core';

@Injectable({
  providedIn: 'root'
})
export class StorageService {
  private readonly STORAGE_KEY = 'cktech-bill-app-state';

  private getLatestInvoiceStorageKey(email: string | null): string {
    return email ? `cktech-bill-latest-invoice-${email}` : 'cktech-bill-latest-invoice-guest';
  }

  private getInvoicesStorageKey(email: string | null): string {
    return email ? `cktech-bill-invoices-${email}` : 'cktech-bill-invoices-guest';
  }

  private getCurrentInvoiceIdStorageKey(email: string | null): string {
    return email ? `cktech-bill-current-invoice-id-${email}` : 'cktech-bill-current-invoice-id-guest';
  }

  private getCompanyProfileStorageKey(email: string | null): string {
    return email ? `cktech-bill-company-profile-${email}` : 'cktech-bill-company-profile-guest';
  }

  private getGeneralSettingsStorageKey(email: string | null): string {
    return email ? `cktech-bill-general-settings-${email}` : 'cktech-bill-general-settings-guest';
  }

  private getPrintTemplateStorageKey(email: string | null): string {
    return email ? `cktech-bill-print-template-${email}` : 'cktech-bill-print-template-guest';
  }

  private getPrintTemplateAccentStorageKey(email: string | null): string {
    return email ? `cktech-bill-print-template-accent-${email}` : 'cktech-bill-print-template-accent-guest';
  }

  private getDocumentLabelStorageKey(email: string | null): string {
    return email ? `cktech-bill-document-label-${email}` : 'cktech-bill-document-label-guest';
  }

  private getInvoiceSettingsStorageKey(email: string | null): string {
    return email ? `cktech-bill-invoice-settings-${email}` : 'cktech-bill-invoice-settings-guest';
  }

  private defaultGeneralSettings = {
    dateFormat: 'dd - mmm - yyyy',
    decimalPlaces: '0',
    quantityDecimals: '0',
    prefixSeparator: '-',
    primarySignature: '',
    secondarySignature: '',
    fiscalYearDate: '',
    uploadedFileName: '',
    toggles: {
      roundOffAmount: true,
      indianFormatting: true,
      inventoryTracking: true,
      shippingCosts: true,
      bankDetails: true,
      salesUserEdit: false,
      openingQuantityEdit: false,
      editTaxLineItem: true,
      duplicateItemName: false,
      currentQuantityEdit: false,
      ownDocumentsOnly: false,
      documentIdentifierHash: true,
      serviceQuantityColumn: false,
      keralaCess: false,
      showPendingAmount: true,
      showCurrencySymbol: true,
    },
  };

  public readonly DEFAULT_INVOICE_SETTINGS = {
    showShippingAddress: true,
    showPrimarySignature: true,
    showSecondarySignature: true,
    decimalPlaces: '0',
    quantityDecimalPlaces: '0',
    roundOffAmount: true,
    showTotalTaxAmount: true,
    showBankDetails: true,
    showPaymentMode: true,
    showTaxDetailsOnThermal: false,
    enableDigitalSignature: false,
    documentPrefixSeparator: '-',
    showFullHeaderOnAllPages: false,
  };

  public loadAppState(): any {
    try {
      const raw = localStorage.getItem(this.STORAGE_KEY);
      if (!raw) {
        return {
          isAuthenticated: false,
          invoice: {},
          registeredUser: null,
          user: null,
        };
      }

      const parsed = JSON.parse(raw);
      return {
        isAuthenticated: Boolean(parsed.isAuthenticated),
        invoice: parsed.invoice?.items?.length ? parsed.invoice : {},
        activeSection: parsed.activeSection === 'invoices' ? 'invoices' : 'dashboard',
        registeredUser: parsed.registeredUser || null,
        user: parsed.user || null,
      };
    } catch {
      return {
        isAuthenticated: false,
        invoice: {},
        activeSection: 'dashboard',
        registeredUser: null,
        user: null,
      };
    }
  }

  public saveAppState(state: any): void {
    localStorage.setItem(this.STORAGE_KEY, JSON.stringify(state));
  }

  public saveLatestInvoice(email: string | null, invoice: any): void {
    localStorage.setItem(this.getLatestInvoiceStorageKey(email), JSON.stringify(invoice));
  }

  public loadInvoices(email: string | null): any[] {
    try {
      const raw = localStorage.getItem(this.getInvoicesStorageKey(email));
      const parsed = raw ? JSON.parse(raw) : [];
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  public saveInvoiceRecord(email: string | null, invoice: any): void {
    const invoices = this.loadInvoices(email);
    const nextInvoices = [invoice, ...invoices.filter((entry) => entry?.id !== invoice?.id)];
    localStorage.setItem(this.getInvoicesStorageKey(email), JSON.stringify(nextInvoices));
    this.saveLatestInvoice(email, invoice);
    localStorage.setItem(this.getCurrentInvoiceIdStorageKey(email), String(invoice?.id || ''));
  }

  public loadLatestInvoice(email: string | null): any {
    try {
      const raw = localStorage.getItem(this.getLatestInvoiceStorageKey(email));
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  }

  public clearLatestInvoice(email: string | null): void {
    localStorage.removeItem(this.getLatestInvoiceStorageKey(email));
  }

  public setCurrentInvoiceId(email: string | null, invoiceId: string): void {
    localStorage.setItem(this.getCurrentInvoiceIdStorageKey(email), String(invoiceId || ''));
  }

  public getCurrentInvoiceId(email: string | null): string {
    try {
      return localStorage.getItem(this.getCurrentInvoiceIdStorageKey(email)) || '';
    } catch {
      return '';
    }
  }

  public loadCurrentInvoice(email: string | null): any {
    const currentInvoiceId = this.getCurrentInvoiceId(email);
    if (currentInvoiceId) {
      const matchingInvoice = this.loadInvoices(email).find((invoice) => String(invoice?.id) === String(currentInvoiceId));
      if (matchingInvoice) return matchingInvoice;
    }
    return this.loadLatestInvoice(email);
  }

  public getProfileStorageKey(email: string | null): string {
    return this.getCompanyProfileStorageKey(email);
  }

  public getGeneralStorageKey(email: string | null): string {
    return this.getGeneralSettingsStorageKey(email);
  }

  public getDefaultGeneralSettings(): any {
    return {
      ...this.defaultGeneralSettings,
      toggles: { ...this.defaultGeneralSettings.toggles },
    };
  }

  public loadGeneralSettings(email: string | null): any {
    try {
      const raw = localStorage.getItem(this.getGeneralSettingsStorageKey(email));
      if (!raw) return this.getDefaultGeneralSettings();
      const parsed = JSON.parse(raw);
      return {
        ...this.getDefaultGeneralSettings(),
        ...parsed,
        toggles: {
          ...this.defaultGeneralSettings.toggles,
          ...(parsed?.toggles || {}),
        },
      };
    } catch {
      return this.getDefaultGeneralSettings();
    }
  }

  public loadPrintTemplate(email: string | null): string {
    try {
      return localStorage.getItem(this.getPrintTemplateStorageKey(email)) || 'gst1';
    } catch {
      return 'gst1';
    }
  }

  public savePrintTemplate(email: string | null, templateId: string): void {
    localStorage.setItem(this.getPrintTemplateStorageKey(email), String(templateId || 'gst1'));
  }

  public loadPrintTemplateAccent(email: string | null): string {
    try {
      return localStorage.getItem(this.getPrintTemplateAccentStorageKey(email)) || '#3f3b3b';
    } catch {
      return '#3f3b3b';
    }
  }

  public savePrintTemplateAccent(email: string | null, accent: string): void {
    localStorage.setItem(this.getPrintTemplateAccentStorageKey(email), String(accent || '#3f3b3b'));
  }

  public loadDocumentLabel(email: string | null): string {
    try {
      return localStorage.getItem(this.getDocumentLabelStorageKey(email)) || 'original';
    } catch {
      return 'original';
    }
  }

  public saveDocumentLabel(email: string | null, label: string): void {
    localStorage.setItem(this.getDocumentLabelStorageKey(email), String(label || 'original'));
  }

  public getInvoicePrefixSeparator(settings: any): string {
    const separator = settings?.prefixSeparator;
    if (separator === '<SPACE>') return ' ';
    if (separator === '<NONE>') return '';
    return separator || '-';
  }

  public formatInvoiceIdentifier(prefix: string, number: string, settings: any): string {
    const safePrefix = String(prefix || '').trim();
    const safeNumber = String(number || '').trim();
    if (!safePrefix) return safeNumber;
    if (!safeNumber) return safePrefix;
    return `${safePrefix}${this.getInvoicePrefixSeparator(settings)}${safeNumber}`;
  }

  public loadInvoiceSettings(email: string | null): any {
    try {
      const raw = localStorage.getItem(this.getInvoiceSettingsStorageKey(email));
      if (!raw) return { ...this.DEFAULT_INVOICE_SETTINGS };
      return { ...this.DEFAULT_INVOICE_SETTINGS, ...JSON.parse(raw) };
    } catch {
      return { ...this.DEFAULT_INVOICE_SETTINGS };
    }
  }

  public saveInvoiceSettings(email: string | null, settings: any): void {
    localStorage.setItem(this.getInvoiceSettingsStorageKey(email), JSON.stringify(settings));
  }
}
